<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import { productsApi } from 'dashboard/api/products';

const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const hasFeature = computed(() =>
  isFeatureEnabled.value(accountId.value, 'sky_products')
);

// ── State ──
const products = ref([]);
const loading = ref(false);
const search = ref('');
const activeTab = ref('catalog');
const showModal = ref(false);
const saving = ref(false);
const editingProduct = ref(null);
const confirmDelete = ref(null);

// ── Simulator state ──
const simProduct = ref(null);
const simTotalValue = ref(0);
const simSetupValue = ref(0);
const simInstallments = ref(1);
const simInstallmentValue = ref(0);
const simCommissionPct = ref(15);
const simCommissionValue = ref(0);
const simNotes = ref('');
const simCopied = ref(false);

const CATEGORIES = [
  {
    key: 'ia_intellichat',
    label: 'Solucoes de IA + IntelliChat',
    icon: 'i-lucide-brain',
    color: 'emerald',
    gradient: 'from-emerald-500 to-teal-600',
    modalities: ['setup_mensalidade', 'anual_cartao', 'anual_pix'],
    subLabels: {
      setup_mensalidade: 'Setup + Mensalidades',
      anual_cartao: 'Anual 10x Cartao',
      anual_pix: 'Anual PIX',
    },
    subColors: {
      setup_mensalidade: 'border-emerald-200 bg-emerald-50',
      anual_cartao: 'border-blue-200 bg-blue-50',
      anual_pix: 'border-violet-200 bg-violet-50',
    },
  },
  {
    key: 'coex_api',
    label: 'COEX - API Oficial',
    icon: 'i-lucide-smartphone',
    color: 'green',
    gradient: 'from-green-600 to-green-700',
    modalities: ['coex_api'],
  },
  {
    key: 'assinatura_intellichat',
    label: 'Assinatura IntelliChat',
    icon: 'i-lucide-sparkles',
    color: 'indigo',
    gradient: 'from-indigo-500 to-indigo-600',
    modalities: ['assinatura_intellichat'],
  },
  {
    key: 'ia_sob_medida',
    label: 'Solucoes de IA Sob Medida',
    icon: 'i-lucide-puzzle',
    color: 'amber',
    gradient: 'from-amber-500 to-amber-600',
    modalities: ['ia_sob_medida'],
  },
];

const COMMISSION_PRESETS = [10, 12, 15, 20];

const emptyForm = () => ({
  code: '',
  name: '',
  description: '',
  modality: 'setup_mensalidade',
  leads_quantity: 1000,
  setup_value: 0,
  annual_value: 0,
  discount_percentage: 0,
  final_value: 0,
  installments_default: 1,
  commission_type: 'percentage',
  commission_value: 15,
  color: '#10b981',
  active: true,
  payment_plans: [],
});

const form = ref(emptyForm());

// Planos de pagamento (variáveis) — combinações que o consultor pode negociar (ex.: entrada PIX +
// saldo cartão). Guardados em products.metadata.payment_plans; consumidos pelo card e pela cobrança.
function addPlan() {
  form.value.payment_plans.push({
    label: '',
    entry: { method: 'none', pct: 0 },
    balance: { method: 'credit_card', installments: 1 },
    default_commission_pct: form.value.commission_value || 15,
  });
}
function removePlan(idx) {
  form.value.payment_plans.splice(idx, 1);
}

// Aninha payment_plans em metadata (preservando metadata existente do produto) para o backend.
function buildPayload() {
  const { payment_plans: plans, ...rest } = form.value;
  return {
    ...rest,
    metadata: {
      ...(editingProduct.value?.metadata || {}),
      payment_plans: plans,
    },
  };
}

// ── Computed ──
const filtered = computed(() => {
  const q = search.value.toLowerCase();
  if (!q) return products.value;
  return products.value.filter(
    p =>
      p.name.toLowerCase().includes(q) ||
      p.code.toLowerCase().includes(q) ||
      (t(`SKY_PRODUCTS.MODALITIES.${p.modality}`) || '')
        .toLowerCase()
        .includes(q)
  );
});

const groupedByCategory = computed(() => {
  return CATEGORIES.map(cat => {
    const catProducts = filtered.value.filter(p =>
      cat.modalities.includes(p.modality || 'general')
    );
    const subGroups =
      cat.modalities.length > 1
        ? cat.modalities
            .map(mod => ({
              key: mod,
              label: cat.subLabels?.[mod] || mod,
              cssClass: cat.subColors?.[mod] || '',
              products: catProducts
                .filter(p => p.modality === mod)
                .sort(
                  (a, b) => (a.leads_quantity || 0) - (b.leads_quantity || 0)
                ),
            }))
            .filter(sg => sg.products.length > 0)
        : null;
    return {
      ...cat,
      allProducts: catProducts.sort(
        (a, b) => (a.leads_quantity || 0) - (b.leads_quantity || 0)
      ),
      subGroups,
    };
  }).filter(g => g.allProducts.length > 0);
});

const totalActive = computed(() => products.value.filter(p => p.active).length);

const totalCatalogValue = computed(() =>
  products.value.reduce(
    (sum, p) => sum + (parseFloat(p.final_value) || parseFloat(p.price) || 0),
    0
  )
);

// ── Modality color for form ──
const MODALITY_COLORS = {
  setup_mensalidade: '#10b981',
  anual_cartao: '#3b82f6',
  anual_pix: '#8b5cf6',
  coex_api: '#059669',
  assinatura_intellichat: '#6366f1',
  ia_sob_medida: '#f59e0b',
  general: '#64748b',
};

// ── API ──
async function fetchProducts() {
  loading.value = true;
  try {
    const res = await productsApi.list();
    products.value = res.data?.payload || [];
  } catch {
    useAlert(t('SKY_PRODUCTS.TOAST.ERROR'));
  } finally {
    loading.value = false;
  }
}

function openNew() {
  editingProduct.value = null;
  form.value = emptyForm();
  showModal.value = true;
}

function openEdit(product) {
  editingProduct.value = product;
  form.value = {
    code: product.code,
    name: product.name,
    description: product.description || '',
    modality: product.modality || 'general',
    leads_quantity: product.leads_quantity || 0,
    setup_value: product.setup_value || 0,
    annual_value: product.annual_value || 0,
    discount_percentage: product.discount_percentage || 0,
    final_value: product.final_value || 0,
    installments_default: product.installments_default || 1,
    commission_type: product.commission_type || 'percentage',
    commission_value: product.commission_value || 15,
    color: product.color || '#3b82f6',
    active: product.active,
    payment_plans: (product.metadata && product.metadata.payment_plans) || [],
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  editingProduct.value = null;
}

async function saveProduct() {
  saving.value = true;
  try {
    if (editingProduct.value) {
      const res = await productsApi.update(
        editingProduct.value.id,
        buildPayload()
      );
      const idx = products.value.findIndex(
        p => p.id === editingProduct.value.id
      );
      if (idx !== -1) products.value[idx] = res.data;
      useAlert(t('SKY_PRODUCTS.TOAST.UPDATED'));
    } else {
      const res = await productsApi.create(buildPayload());
      products.value.push(res.data);
      useAlert(t('SKY_PRODUCTS.TOAST.CREATED'));
    }
    closeModal();
  } catch {
    useAlert(t('SKY_PRODUCTS.TOAST.ERROR'));
  } finally {
    saving.value = false;
  }
}

async function toggleActive(product) {
  try {
    const res = await productsApi.update(product.id, {
      active: !product.active,
    });
    const idx = products.value.findIndex(p => p.id === product.id);
    if (idx !== -1) products.value[idx] = res.data;
  } catch {
    useAlert(t('SKY_PRODUCTS.TOAST.ERROR'));
  }
}

function askDelete(product) {
  confirmDelete.value = product;
}

async function doDelete() {
  if (!confirmDelete.value) return;
  try {
    await productsApi.delete(confirmDelete.value.id);
    products.value = products.value.filter(
      p => p.id !== confirmDelete.value.id
    );
    useAlert(t('SKY_PRODUCTS.TOAST.DELETED'));
  } catch {
    useAlert(t('SKY_PRODUCTS.TOAST.ERROR'));
  } finally {
    confirmDelete.value = null;
  }
}

// ── Form watchers ──
watch(
  () => form.value.modality,
  mod => {
    form.value.color = MODALITY_COLORS[mod] || '#64748b';
    if (mod === 'anual_pix') {
      form.value.discount_percentage = 10;
    } else {
      form.value.discount_percentage = 0;
    }
    recalcFinalValue();
  }
);

watch(() => form.value.annual_value, recalcFinalValue);
watch(() => form.value.setup_value, recalcFinalValue);
watch(() => form.value.discount_percentage, recalcFinalValue);

function recalcFinalValue() {
  const annual = parseFloat(form.value.annual_value) || 0;
  const setup = parseFloat(form.value.setup_value) || 0;
  const disc = parseFloat(form.value.discount_percentage) || 0;
  const discountedAnnual = annual * (1 - disc / 100);
  form.value.final_value = Math.round((setup + discountedAnnual) * 100) / 100;
}

// ── Simulator ──
function selectSimProduct(product) {
  simProduct.value = product;
  simTotalValue.value =
    parseFloat(product.final_value) || parseFloat(product.price) || 0;
  simSetupValue.value = parseFloat(product.setup_value) || 0;
  simInstallments.value = product.installments_default || 1;
  simCommissionPct.value = parseFloat(product.commission_value) || 15;
  recalcSim();
}

function recalcSim() {
  const total = parseFloat(simTotalValue.value) || 0;
  const inst = parseInt(simInstallments.value, 10) || 1;
  simInstallmentValue.value = Math.round((total / inst) * 100) / 100;
  simCommissionValue.value =
    Math.round(total * (parseFloat(simCommissionPct.value) || 0)) / 100;
}

function recalcSimFromInstallment() {
  const instVal = parseFloat(simInstallmentValue.value) || 0;
  const inst = parseInt(simInstallments.value, 10) || 1;
  simTotalValue.value = Math.round(instVal * inst * 100) / 100;
  simCommissionValue.value =
    Math.round(
      simTotalValue.value * (parseFloat(simCommissionPct.value) || 0)
    ) / 100;
}

function setCommissionPreset(pct) {
  simCommissionPct.value = pct;
  recalcSim();
}

function copyProposal() {
  if (!simProduct.value) return;
  const mod = t(`SKY_PRODUCTS.MODALITIES.${simProduct.value.modality}`);
  const lines = [
    `PROPOSTA COMERCIAL SKY`,
    ``,
    `Produto: ${simProduct.value.name}`,
    `Modalidade: ${mod}`,
    `Leads: ${(simProduct.value.leads_quantity || 0).toLocaleString('pt-BR')}`,
    ``,
    simSetupValue.value > 0
      ? `Setup: ${formatCurrency(simSetupValue.value)}`
      : null,
    `Valor total: ${formatCurrency(simTotalValue.value)}`,
    `Pagamento: ${simInstallments.value}x de ${formatCurrency(simInstallmentValue.value)}`,
    ``,
    `Comissao (${simCommissionPct.value}%): ${formatCurrency(simCommissionValue.value)}`,
    simNotes.value ? `\nObs: ${simNotes.value}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  navigator.clipboard.writeText(lines);
  simCopied.value = true;
  setTimeout(() => {
    simCopied.value = false;
  }, 2000);
}

// ── Helpers ──
function formatCurrency(val) {
  return Number(val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatLeads(val) {
  return val ? val.toLocaleString('pt-BR') : '-';
}

onMounted(fetchProducts);
</script>

<template>
  <div class="flex h-full flex-col overflow-hidden">
    <FeatureLockedScreen v-if="!hasFeature" feature-name="sky_products" />

    <template v-else>
      <!-- Header -->
      <div
        class="flex items-center justify-between border-b border-n-weak px-6 py-4"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SKY_PRODUCTS.TITLE') }}
          </h1>
          <p class="mt-0.5 text-sm text-n-slate-11">
            {{ t('SKY_PRODUCTS.SUBTITLE') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <!-- Stats pills -->
          <div
            class="flex items-center gap-1.5 rounded-full bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11"
          >
            <span class="i-lucide-package text-sm" />
            {{ totalActive }} ativos
          </div>
          <button
            class="flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90"
            @click="openNew"
          >
            <i class="i-lucide-plus size-4" />
            {{ t('SKY_PRODUCTS.ADD_BUTTON') }}
          </button>
        </div>
      </div>

      <!-- Tabs + Search -->
      <div
        class="flex items-center justify-between border-b border-n-weak px-6"
      >
        <div class="flex gap-0">
          <button
            v-for="tab in ['catalog', 'simulator']"
            :key="tab"
            class="relative px-4 py-3 text-sm font-medium transition-colors"
            :class="
              activeTab === tab
                ? 'text-n-brand after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-n-brand'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="activeTab = tab"
          >
            {{ t(`SKY_PRODUCTS.TABS.${tab.toUpperCase()}`) }}
          </button>
        </div>
        <div v-if="activeTab === 'catalog'" class="relative py-2">
          <i
            class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="t('SKY_PRODUCTS.SEARCH_PLACEHOLDER')"
            class="w-72 rounded-lg border border-n-weak bg-n-background py-2 pl-9 pr-3 text-sm text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- TAB: Catalog -->
      <div
        v-if="activeTab === 'catalog'"
        class="flex-1 overflow-auto px-6 py-5"
      >
        <div v-if="loading" class="flex h-40 items-center justify-center">
          <i class="i-lucide-loader-2 size-6 animate-spin text-n-slate-10" />
        </div>

        <div
          v-else-if="groupedByCategory.length === 0"
          class="flex h-40 flex-col items-center justify-center text-n-slate-10"
        >
          <i class="i-lucide-package mb-2 size-10" />
          <span class="text-sm">{{ t('SKY_PRODUCTS.EMPTY_STATE') }}</span>
        </div>

        <div v-else class="space-y-8">
          <div v-for="cat in groupedByCategory" :key="cat.key">
            <!-- Category header -->
            <div class="mb-4 flex items-center gap-3">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm"
                :class="cat.gradient"
              >
                <span :class="cat.icon" class="text-lg" />
              </div>
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ cat.label }}
                </h2>
              </div>
              <span
                class="rounded-full bg-n-alpha-2 px-2.5 py-0.5 text-[10px] font-semibold text-n-slate-10"
              >
                {{ cat.allProducts.length }} produtos
              </span>
            </div>

            <!-- Sub-grouped (e.g. IA+IntelliChat has 3 sub-modalities) -->
            <template v-if="cat.subGroups">
              <div
                v-for="sub in cat.subGroups"
                :key="sub.key"
                class="mb-4 last:mb-0"
              >
                <div
                  class="mb-2 ml-1 flex items-center gap-2 rounded-lg border px-3 py-1.5"
                  :class="sub.cssClass || 'border-n-alpha-3 bg-n-alpha-1'"
                >
                  <span class="text-xs font-semibold text-n-slate-11">{{
                    sub.label
                  }}</span>
                  <span class="text-[10px] text-n-slate-9">({{ sub.products.length }})</span>
                </div>
                <div
                  class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
                >
                  <div
                    v-for="product in sub.products"
                    :key="product.id"
                    class="group relative rounded-xl border border-n-alpha-3 bg-n-solid-1 p-4 transition-all hover:border-n-alpha-5 hover:shadow-md"
                    :class="{ 'opacity-50': !product.active }"
                  >
                    <div class="mb-3 flex items-start justify-between">
                      <span
                        v-if="product.leads_quantity"
                        class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                      >
                        <span class="i-lucide-users text-[10px]" />
                        {{ formatLeads(product.leads_quantity) }}
                      </span>
                      <div
                        class="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <button
                          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                          @click="openEdit(product)"
                        >
                          <i class="i-lucide-pencil size-3.5" />
                        </button>
                        <button
                          class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-ruby-9"
                          @click="askDelete(product)"
                        >
                          <i class="i-lucide-trash-2 size-3.5" />
                        </button>
                      </div>
                    </div>
                    <p class="text-2xl font-bold text-n-slate-12">
                      {{ formatCurrency(product.final_value || product.price) }}
                    </p>
                    <p
                      v-if="
                        product.annual_value > 0 &&
                        product.final_value < product.annual_value
                      "
                      class="mt-0.5 text-xs text-n-slate-9 line-through"
                    >
                      {{ formatCurrency(product.annual_value) }}
                    </p>
                    <div class="mt-3 space-y-1.5 text-xs text-n-slate-10">
                      <div
                        v-if="product.setup_value > 0"
                        class="flex items-center justify-between"
                      >
                        <span>Setup</span><span class="font-medium text-n-slate-12">{{
                          formatCurrency(product.setup_value)
                        }}</span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span>Parcelas</span><span class="font-medium text-n-slate-12">{{ product.installments_default }}x</span>
                      </div>
                      <div
                        v-if="product.discount_percentage > 0"
                        class="flex items-center justify-between"
                      >
                        <span>Desconto</span><span class="font-semibold text-violet-600">{{ product.discount_percentage }}%</span>
                      </div>
                      <div class="flex items-center justify-between">
                        <span>Comissao</span><span class="font-semibold text-amber-600">{{ product.commission_value }}%</span>
                      </div>
                    </div>
                    <div
                      class="mt-3 flex items-center justify-between border-t border-n-alpha-2 pt-3"
                    >
                      <button
                        class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                        :class="product.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                        @click="toggleActive(product)"
                      >
                        <span
                          class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
                          :class="
                            product.active ? 'translate-x-4' : 'translate-x-0.5'
                          "
                        />
                      </button>
                      <button
                        class="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-n-brand hover:bg-n-blue-2/20"
                        @click="
                          activeTab = 'simulator';
                          selectSimProduct(product);
                        "
                      >
                        <span class="i-lucide-calculator text-xs" /> Simular
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Flat category (COEX, IntelliChat, IA Sob Medida) -->
            <template v-else>
              <div
                class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              >
                <div
                  v-for="product in cat.allProducts"
                  :key="product.id"
                  class="group relative rounded-xl border border-n-alpha-3 bg-n-solid-1 p-4 transition-all hover:border-n-alpha-5 hover:shadow-md"
                  :class="{ 'opacity-50': !product.active }"
                >
                  <div class="mb-3 flex items-start justify-between">
                    <span
                      v-if="product.leads_quantity"
                      class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold text-n-slate-11"
                    >
                      <span class="i-lucide-users text-[10px]" />
                      {{ formatLeads(product.leads_quantity) }}
                    </span>
                    <span v-else />
                    <div
                      class="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <button
                        class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                        @click="openEdit(product)"
                      >
                        <i class="i-lucide-pencil size-3.5" />
                      </button>
                      <button
                        class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-ruby-9"
                        @click="askDelete(product)"
                      >
                        <i class="i-lucide-trash-2 size-3.5" />
                      </button>
                    </div>
                  </div>
                  <p class="mb-0.5 text-sm font-medium text-n-slate-11">
                    {{ product.name }}
                  </p>
                  <p
                    v-if="product.final_value > 0 || product.price > 0"
                    class="text-2xl font-bold text-n-slate-12"
                  >
                    {{ formatCurrency(product.final_value || product.price) }}
                  </p>
                  <p v-else class="text-lg font-semibold text-amber-600">
                    Valor em aberto
                  </p>
                  <div class="mt-3 space-y-1.5 text-xs text-n-slate-10">
                    <div
                      v-if="product.installments_default > 1"
                      class="flex items-center justify-between"
                    >
                      <span>Parcelas</span><span class="font-medium text-n-slate-12">{{ product.installments_default }}x de
                        {{
                          formatCurrency(
                            (product.final_value || product.price) /
                              product.installments_default
                          )
                        }}</span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span>Comissao</span><span class="font-semibold text-amber-600">{{ product.commission_value }}%</span>
                    </div>
                    <p
                      v-if="product.description"
                      class="mt-1 text-[11px] leading-relaxed text-n-slate-9"
                    >
                      {{ product.description }}
                    </p>
                  </div>
                  <div
                    class="mt-3 flex items-center justify-between border-t border-n-alpha-2 pt-3"
                  >
                    <button
                      class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                      :class="product.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                      @click="toggleActive(product)"
                    >
                      <span
                        class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
                        :class="
                          product.active ? 'translate-x-4' : 'translate-x-0.5'
                        "
                      />
                    </button>
                    <button
                      class="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-n-brand hover:bg-n-blue-2/20"
                      @click="
                        activeTab = 'simulator';
                        selectSimProduct(product);
                      "
                    >
                      <span class="i-lucide-calculator text-xs" /> Simular
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- TAB: Simulator -->
      <div
        v-if="activeTab === 'simulator'"
        class="flex-1 overflow-auto px-6 py-5"
      >
        <div class="mx-auto max-w-4xl space-y-6">
          <!-- Product selector -->
          <div class="rounded-xl border border-n-alpha-3 bg-n-solid-1 p-5">
            <p
              class="mb-3 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
            >
              {{ t('SKY_PRODUCTS.SIMULATOR.SELECT_PRODUCT') }}
            </p>
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <button
                v-for="p in products.filter(x => x.active)"
                :key="p.id"
                type="button"
                class="flex items-center gap-3 rounded-lg border p-3 text-left transition-all"
                :class="
                  simProduct?.id === p.id
                    ? 'border-n-blue-7/40 bg-n-blue-2/10 ring-1 ring-n-blue-7/20'
                    : 'border-n-alpha-3 hover:border-n-alpha-4'
                "
                @click="selectSimProduct(p)"
              >
                <span
                  class="inline-block h-3 w-3 shrink-0 rounded-full"
                  :style="{ backgroundColor: p.color }"
                />
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ p.name }}
                  </p>
                  <p class="text-xs text-n-slate-9">
                    {{ formatCurrency(p.final_value || p.price) }}
                  </p>
                </div>
                <span
                  v-if="simProduct?.id === p.id"
                  class="i-lucide-check shrink-0 text-sm text-n-blue-9"
                />
              </button>
            </div>
          </div>

          <!-- Simulator controls -->
          <div v-if="simProduct" class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <!-- Left: inputs -->
            <div
              class="space-y-4 rounded-xl border border-n-alpha-3 bg-n-solid-1 p-5"
            >
              <div class="flex items-center gap-2">
                <span class="i-lucide-settings-2 text-sm text-n-slate-10" />
                <p
                  class="text-xs font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  Configurar Pagamento
                </p>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label
                    class="mb-1 block text-[11px] font-medium text-n-slate-10"
                  >
                    {{ t('SKY_PRODUCTS.SIMULATOR.TOTAL_VALUE') }}
                  </label>
                  <input
                    v-model.number="simTotalValue"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-right text-sm font-semibold text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @input="recalcSim"
                  />
                </div>
                <div>
                  <label
                    class="mb-1 block text-[11px] font-medium text-n-slate-10"
                  >
                    {{ t('SKY_PRODUCTS.SIMULATOR.SETUP_VALUE') }}
                  </label>
                  <input
                    v-model.number="simSetupValue"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-right text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                  />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label
                    class="mb-1 block text-[11px] font-medium text-n-slate-10"
                  >
                    {{ t('SKY_PRODUCTS.SIMULATOR.INSTALLMENTS') }}
                  </label>
                  <input
                    v-model.number="simInstallments"
                    type="number"
                    step="1"
                    min="1"
                    max="36"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-right text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @input="recalcSim"
                  />
                </div>
                <div>
                  <label
                    class="mb-1 block text-[11px] font-medium text-n-slate-10"
                  >
                    {{ t('SKY_PRODUCTS.SIMULATOR.INSTALLMENT_VALUE') }}
                  </label>
                  <input
                    v-model.number="simInstallmentValue"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-right text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @input="recalcSimFromInstallment"
                  />
                </div>
              </div>

              <!-- Commission presets -->
              <div>
                <label
                  class="mb-1 block text-[11px] font-medium text-n-slate-10"
                >
                  {{ t('SKY_PRODUCTS.SIMULATOR.COMMISSION_PCT') }}
                </label>
                <div class="flex items-center gap-2">
                  <button
                    v-for="pct in COMMISSION_PRESETS"
                    :key="pct"
                    type="button"
                    class="rounded-lg border px-3 py-1.5 text-sm font-medium transition-all"
                    :class="
                      simCommissionPct === pct
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-n-alpha-3 text-n-slate-11 hover:border-n-alpha-5'
                    "
                    @click="setCommissionPreset(pct)"
                  >
                    {{ pct }}%
                  </button>
                  <input
                    v-model.number="simCommissionPct"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    class="w-20 rounded-lg border border-n-alpha-4 bg-n-background px-2 py-1.5 text-center text-sm text-n-slate-12 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                    @input="recalcSim"
                  />
                </div>
              </div>

              <!-- Notes -->
              <div>
                <label
                  class="mb-1 block text-[11px] font-medium text-n-slate-10"
                >
                  {{ t('SKY_PRODUCTS.SIMULATOR.PAYMENT_NOTES') }}
                </label>
                <textarea
                  v-model="simNotes"
                  rows="2"
                  :placeholder="
                    t('SKY_PRODUCTS.SIMULATOR.PAYMENT_NOTES_PLACEHOLDER')
                  "
                  class="w-full resize-none rounded-lg border border-n-alpha-4 bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-8 focus:border-n-blue-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7/30"
                />
              </div>
            </div>

            <!-- Right: proposal summary -->
            <div class="space-y-4">
              <!-- Big summary card -->
              <div
                class="rounded-xl border border-n-alpha-3 bg-gradient-to-br from-n-slate-1 to-n-background p-5"
              >
                <p
                  class="mb-4 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
                >
                  {{ t('SKY_PRODUCTS.SIMULATOR.RESULT_TITLE') }}
                </p>

                <div class="mb-4">
                  <p class="text-sm text-n-slate-10">{{ simProduct.name }}</p>
                  <p class="text-xs text-n-slate-9">
                    {{ t(`SKY_PRODUCTS.MODALITIES.${simProduct.modality}`) }}
                    &#x2022; {{ formatLeads(simProduct.leads_quantity) }} leads
                  </p>
                </div>

                <div class="space-y-3">
                  <div
                    v-if="simSetupValue > 0"
                    class="flex items-center justify-between rounded-lg bg-n-alpha-1 p-3"
                  >
                    <div
                      class="flex items-center gap-2 text-sm text-n-slate-10"
                    >
                      <span class="i-lucide-hard-drive text-base" />
                      Setup
                    </div>
                    <span class="text-sm font-semibold text-n-slate-12">{{
                      formatCurrency(simSetupValue)
                    }}</span>
                  </div>

                  <div
                    class="flex items-center justify-between rounded-lg bg-emerald-50 p-3"
                  >
                    <div
                      class="flex items-center gap-2 text-sm text-emerald-700"
                    >
                      <span class="i-lucide-dollar-sign text-base" />
                      Valor Total
                    </div>
                    <span class="text-lg font-bold text-emerald-700">{{
                      formatCurrency(simTotalValue)
                    }}</span>
                  </div>

                  <div
                    class="flex items-center justify-between rounded-lg bg-blue-50 p-3"
                  >
                    <div class="flex items-center gap-2 text-sm text-blue-700">
                      <span class="i-lucide-split text-base" />
                      Parcelamento
                    </div>
                    <span class="text-lg font-bold text-blue-700">
                      {{ simInstallments }}x
                      {{ formatCurrency(simInstallmentValue) }}
                    </span>
                  </div>

                  <div
                    class="flex items-center justify-between rounded-lg bg-amber-50 p-3"
                  >
                    <div class="flex items-center gap-2 text-sm text-amber-700">
                      <span class="i-lucide-trending-up text-base" />
                      Comissao ({{ simCommissionPct }}%)
                    </div>
                    <span class="text-lg font-bold text-amber-700">{{
                      formatCurrency(simCommissionValue)
                    }}</span>
                  </div>
                </div>

                <div v-if="simNotes" class="mt-3 rounded-lg bg-n-alpha-1 p-3">
                  <p class="text-[11px] font-medium text-n-slate-10">
                    Observacoes:
                  </p>
                  <p class="mt-0.5 text-sm text-n-slate-12">{{ simNotes }}</p>
                </div>
              </div>

              <!-- Copy button -->
              <button
                type="button"
                class="flex w-full items-center justify-center gap-2 rounded-lg bg-n-brand px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-n-brand/90"
                @click="copyProposal"
              >
                <span
                  :class="simCopied ? 'i-lucide-check' : 'i-lucide-copy'"
                  class="text-sm"
                />
                {{
                  simCopied
                    ? t('SKY_PRODUCTS.SIMULATOR.COPIED')
                    : t('SKY_PRODUCTS.SIMULATOR.COPY_PROPOSAL')
                }}
              </button>
            </div>
          </div>

          <!-- Empty state for simulator -->
          <div
            v-if="!simProduct"
            class="flex flex-col items-center py-16 text-n-slate-10"
          >
            <span class="i-lucide-calculator mb-3 text-4xl opacity-30" />
            <p class="text-sm">{{ t('SKY_PRODUCTS.SIMULATOR.SUBTITLE') }}</p>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="closeModal"
      >
        <div
          class="mx-4 w-full max-w-lg rounded-xl bg-n-background p-6 shadow-xl"
        >
          <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
            {{
              editingProduct
                ? t('SKY_PRODUCTS.FORM.TITLE_EDIT')
                : t('SKY_PRODUCTS.FORM.TITLE_NEW')
            }}
          </h2>

          <div class="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <!-- Code + Modality -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.CODE') }}
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  :placeholder="t('SKY_PRODUCTS.FORM.CODE_PLACEHOLDER')"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.MODALITY') }}
                </label>
                <select
                  v-model="form.modality"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                >
                  <optgroup label="Solucoes de IA + IntelliChat">
                    <option value="setup_mensalidade">
                      Setup + Mensalidades
                    </option>
                    <option value="anual_cartao">Anual 10x Cartao</option>
                    <option value="anual_pix">Anual PIX</option>
                  </optgroup>
                  <option value="coex_api">COEX - API Oficial</option>
                  <option value="assinatura_intellichat">
                    Assinatura IntelliChat
                  </option>
                  <option value="ia_sob_medida">
                    Solucoes de IA Sob Medida
                  </option>
                </select>
              </div>
            </div>

            <!-- Name -->
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ t('SKY_PRODUCTS.FORM.NAME') }}
              </label>
              <input
                v-model="form.name"
                type="text"
                :placeholder="t('SKY_PRODUCTS.FORM.NAME_PLACEHOLDER')"
                class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="mb-1 block text-xs font-medium text-n-slate-11">
                {{ t('SKY_PRODUCTS.FORM.DESCRIPTION') }}
              </label>
              <textarea
                v-model="form.description"
                rows="2"
                :placeholder="t('SKY_PRODUCTS.FORM.DESCRIPTION_PLACEHOLDER')"
                class="w-full resize-none rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>

            <!-- Leads + Setup -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.LEADS_QUANTITY') }}
                </label>
                <input
                  v-model.number="form.leads_quantity"
                  type="number"
                  min="0"
                  step="500"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.SETUP_VALUE') }}
                </label>
                <input
                  v-model.number="form.setup_value"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Annual + Discount -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.ANNUAL_VALUE') }}
                </label>
                <input
                  v-model.number="form.annual_value"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.DISCOUNT_PERCENTAGE') }}
                </label>
                <input
                  v-model.number="form.discount_percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Final value (read-only) + Installments -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.FINAL_VALUE') }}
                </label>
                <div
                  class="flex h-[38px] items-center rounded-lg border border-n-alpha-3 bg-n-alpha-1 px-3 text-sm font-semibold text-emerald-600"
                >
                  {{ formatCurrency(form.final_value) }}
                </div>
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.INSTALLMENTS_DEFAULT') }}
                </label>
                <input
                  v-model.number="form.installments_default"
                  type="number"
                  min="1"
                  max="36"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
            </div>

            <!-- Commission + Color + Active -->
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.COMMISSION_PERCENTAGE') }}
                </label>
                <input
                  v-model.number="form.commission_value"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.COLOR') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="form.color"
                    type="color"
                    class="h-[38px] w-12 cursor-pointer rounded border border-n-weak"
                  />
                  <span class="font-mono text-xs text-n-slate-10">{{
                    form.color
                  }}</span>
                </div>
              </div>
              <div>
                <label class="mb-1 block text-xs font-medium text-n-slate-11">
                  {{ t('SKY_PRODUCTS.FORM.ACTIVE') }}
                </label>
                <button
                  class="relative mt-1 inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                  :class="form.active ? 'bg-n-brand' : 'bg-n-slate-6'"
                  type="button"
                  @click="form.active = !form.active"
                >
                  <span
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                    :class="form.active ? 'translate-x-5' : 'translate-x-0.5'"
                  />
                </button>
              </div>
            </div>

            <!-- Planos de pagamento (variáveis) -->
            <div class="rounded-lg border border-n-weak/60 p-3">
              <div class="mb-2 flex items-start justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold text-n-slate-12">
                    {{ t('SKY_PRODUCTS.FORM.PAYMENT_PLANS_TITLE') }}
                  </p>
                  <p class="text-[11px] text-n-slate-9">
                    {{ t('SKY_PRODUCTS.FORM.PAYMENT_PLANS_HINT') }}
                  </p>
                </div>
                <button
                  type="button"
                  class="flex shrink-0 items-center gap-1 rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                  @click="addPlan"
                >
                  <span class="i-lucide-plus" />
                  {{ t('SKY_PRODUCTS.FORM.PLAN_ADD') }}
                </button>
              </div>

              <p
                v-if="!form.payment_plans.length"
                class="text-[11px] text-n-slate-9"
              >
                {{ t('SKY_PRODUCTS.FORM.PLAN_EMPTY') }}
              </p>

              <div
                v-for="(plan, idx) in form.payment_plans"
                :key="idx"
                class="mb-2 rounded-md border border-n-weak/40 p-2"
              >
                <div class="flex items-center gap-2">
                  <input
                    v-model="plan.label"
                    type="text"
                    :placeholder="t('SKY_PRODUCTS.FORM.PLAN_LABEL')"
                    class="w-full rounded border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  />
                  <button
                    type="button"
                    class="shrink-0 text-n-slate-9 hover:text-ruby-9"
                    @click="removePlan(idx)"
                  >
                    <span class="i-lucide-trash-2" />
                  </button>
                </div>
                <div class="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <div>
                    <label class="mb-0.5 block text-[10px] text-n-slate-10">
                      {{ t('SKY_PRODUCTS.FORM.PLAN_ENTRY') }}
                    </label>
                    <select
                      v-model="plan.entry.method"
                      class="w-full rounded border border-n-weak bg-n-background px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    >
                      <option value="none">
                        {{ t('SKY_PRODUCTS.FORM.PLAN_NONE') }}
                      </option>
                      <option value="pix">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_PIX') }}
                      </option>
                      <option value="credit_card">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_CARD') }}
                      </option>
                      <option value="boleto">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_BOLETO') }}
                      </option>
                    </select>
                  </div>
                  <div v-if="plan.entry.method !== 'none'">
                    <label class="mb-0.5 block text-[10px] text-n-slate-10">
                      {{ t('SKY_PRODUCTS.FORM.PLAN_ENTRY_PCT') }}
                    </label>
                    <input
                      v-model.number="plan.entry.pct"
                      type="number"
                      min="0"
                      max="100"
                      class="w-full rounded border border-n-weak bg-n-background px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    />
                  </div>
                  <div>
                    <label class="mb-0.5 block text-[10px] text-n-slate-10">
                      {{ t('SKY_PRODUCTS.FORM.PLAN_BALANCE') }}
                    </label>
                    <select
                      v-model="plan.balance.method"
                      class="w-full rounded border border-n-weak bg-n-background px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    >
                      <option value="pix">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_PIX') }}
                      </option>
                      <option value="credit_card">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_CARD') }}
                      </option>
                      <option value="boleto">
                        {{ t('SKY_PRODUCTS.FORM.METHOD_BOLETO') }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="mb-0.5 block text-[10px] text-n-slate-10">
                      {{ t('SKY_PRODUCTS.FORM.PLAN_INSTALLMENTS') }}
                    </label>
                    <input
                      v-model.number="plan.balance.installments"
                      type="number"
                      min="1"
                      max="36"
                      class="w-full rounded border border-n-weak bg-n-background px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    />
                  </div>
                  <div>
                    <label class="mb-0.5 block text-[10px] text-n-slate-10">
                      {{ t('SKY_PRODUCTS.FORM.PLAN_COMMISSION') }}
                    </label>
                    <input
                      v-model.number="plan.default_commission_pct"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      class="w-full rounded border border-n-weak bg-n-background px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-6 flex justify-end gap-3">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 transition-colors hover:text-n-slate-12"
              @click="closeModal"
            >
              {{ t('SKY_PRODUCTS.FORM.CANCEL') }}
            </button>
            <button
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 disabled:opacity-50"
              :disabled="saving || !form.name || !form.code"
              @click="saveProduct"
            >
              <i
                v-if="saving"
                class="i-lucide-loader-2 mr-1 size-4 animate-spin"
              />
              {{ t('SKY_PRODUCTS.FORM.SAVE') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirm Modal -->
      <div
        v-if="confirmDelete"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="confirmDelete = null"
      >
        <div
          class="mx-4 w-full max-w-sm rounded-xl bg-n-background p-6 shadow-xl"
        >
          <h2 class="mb-2 text-lg font-semibold text-n-slate-12">
            {{ t('SKY_PRODUCTS.CONFIRM_DELETE.TITLE') }}
          </h2>
          <p class="mb-6 text-sm text-n-slate-11">
            {{
              t('SKY_PRODUCTS.CONFIRM_DELETE.MESSAGE', {
                name: confirmDelete.name,
              })
            }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 transition-colors hover:text-n-slate-12"
              @click="confirmDelete = null"
            >
              {{ t('SKY_PRODUCTS.CONFIRM_DELETE.CANCEL') }}
            </button>
            <button
              class="rounded-lg bg-ruby-9 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-ruby-10"
              @click="doDelete"
            >
              {{ t('SKY_PRODUCTS.CONFIRM_DELETE.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
